import { initializeApp, type FirebaseApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore'
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from 'firebase/storage'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions'

/**
 * Firebase initialization. Exports the four singletons the rest of the app
 * imports. Service modules import `db`/`storage` from here; they never call
 * `getFirestore()`/`initializeApp()` themselves.
 *
 * All config comes from Vite env vars (see .env.example). These values are
 * public by design — access control lives in Firestore/Storage rules and Cloud
 * Functions, not in secrecy of the web config.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

export const app: FirebaseApp = initializeApp(firebaseConfig)

// App Check — attests that requests come from the genuine web app (reCAPTCHA v3)
// before Firestore/Storage/Functions accept them. Production only: reCAPTCHA needs
// the real registered domain, and enabling it against localhost/emulators/E2E just
// causes flakiness (it isn't the protection surface anyway). Turn on *enforcement*
// in the Firebase console only after real traffic is sending tokens (watch the
// App Check metrics first) so real users aren't locked out.
//
// The site key is public — safe to ship (it's in the client bundle) and is
// domain-locked; the matching *secret* lives only in the Firebase console.
const appCheckSiteKey =
  import.meta.env.VITE_APPCHECK_RECAPTCHA_KEY || '6LeEO0MtAAAAAAHfm6sFHmO8YlVgTFmzp4wDjtQq'
if (import.meta.env.PROD && appCheckSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  })
}

export const db: Firestore = getFirestore(app)
export const storage: FirebaseStorage = getStorage(app)
export const auth: Auth = getAuth(app)
export const functions: Functions = getFunctions(app)

// Local development against the emulator suite. Guarded so it only runs once
// and only when explicitly opted in via env.
if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectStorageEmulator(storage, '127.0.0.1', 9199)
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
}
